import React, { useState, useEffect } from 'react';

interface AnimatedFreakSpinnerProps {
  size?: number;
  color?: string;
  duration?: number; // Duration for one complete cycle in milliseconds
}

const AnimatedFreakSpinner: React.FC<AnimatedFreakSpinnerProps> = ({
  size = 60,
  color = '#82368c',
  duration = 2000
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Die 4 Pfade aus Zeilen 7-10 des SVGs
  const paths = [
    // Zeile 7
    "M36.52408,13.46889c.29629.10786.53667.33017.66768.61685.13271.2833.1472.60801.04009.90203-.10861.29534-.33073.53516-.61685.66617-.28443.13195-.60951.14532-.90373.03708-.2961-.10767-.5361-.32998-.66617-.61685-.13271-.2833-.1472-.60801-.04009-.90203.10861-.29534.33073-.53516.61685-.66617.15511-.07209.32396-.10955.49506-.10955.13892.00056.2769.0256.40716.07398v-.00151Z",
    // Zeile 8  
    "M33.54786,16.81519c.77196.88585,1.94506,1.31296,3.10573,1.13037.49506-.07755.95964.26052,1.03775.75559.07661.49488-.26109.95869-.75559,1.03775-1.77903.28085-3.57727-.37403-4.75883-1.73329-.32659-.37798-.28593-.94891.09092-1.27682.16508-.14344.37648-.2225.59521-.22212.2624-.00075.51219.11238.685.30984l-.00019-.00132Z",
    // Zeile 9
    "M29.59261,19.1083c.93686,1.34628,2.26732,2.36972,3.8088,2.92992,1.54355.56227,3.22319.63229,4.80815.20047.23191-.06268.47906-.03106.68782.08791.20857.11972.3616.31699.42561.5489.13177.48377-.15323.98279-.63681,1.11493-1.94581.53008-4.00795.44443-5.90294-.24527-1.89555-.68933-3.53058-1.94939-4.68015-3.60682-.28367-.41149-.1809-.97507.22984-1.25987.15247-.10259.33262-.15643.51652-.15417.29534-.00056.57262.14268.74316.38382v.00019h0Z",
    // Zeile 10
    "M25.19011,20.60724c1.45658,2.64493,3.82292,4.67206,6.66003,5.70548,2.83655,1.03229,5.95094,1.00161,8.7666-.0864.46758-.1777.99088.05591,1.17046.52274.18108.46589-.04969.99051-.51577,1.17159-.00019.00019-.00056.00019-.00075.00038-3.22489,1.24557-6.7918,1.28002-10.04021.09713-3.2486-1.18232-5.95885-3.50178-7.62852-6.52902-.24301-.43784-.0849-.98975.35295-1.23277.00056-.00038.00113-.00056.00169-.00094.1344-.07473.28574-.11407.43953-.11407.32942-.00038.63342.17788.79399.46551,0,0,0,.00038,0,.00037Z"
  ];

  useEffect(() => {
    const stepDuration = duration / (paths.length + 1); // +1 für die Pause zwischen Zyklen
    
    const interval = setInterval(() => {
      setCurrentStep(prev => {
        // Nach allen Pfaden eine Pause einlegen, dann von vorn beginnen
        if (prev >= paths.length) {
          return 0;
        }
        return prev + 1;
      });
    }, stepDuration);

    return () => clearInterval(interval);
  }, [duration, paths.length]);

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      width: size,
      height: size 
    }}>
      <svg 
        width={size} 
        height={size} 
        viewBox="0 0 60 60"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g id="AGE_x5F_DOWN">
          {/* Statische Hintergrund-Elemente (Zeilen 5-6) */}
          <rect 
            x="8.07949" 
            y="8.07975" 
            width="43.841" 
            height="43.841" 
            rx="10" 
            ry="10" 
            transform="translate(-12.42659 30.00007) rotate(-45)" 
            fill="#1d1d1b"
          />
          <rect 
            x="10.01836" 
            y="11.83799" 
            width="39.96329" 
            height="39.96329" 
            rx="10" 
            ry="10" 
            transform="translate(-13.71308 30.53296) rotate(-45)" 
            fill="#f59e00"
          />
          
          {/* Animierte Pfade (Zeilen 7-10) */}
          {paths.map((path, index) => (
            <path
              key={index}
              d={path}
              fill={color}
              style={{
                opacity: currentStep > index && currentStep <= paths.length ? 1 : 0,
                transition: 'opacity 0.2s ease-in-out'
              }}
            />
          ))}
          
          {/* Großer Pfad am Ende (Zeile 11) - immer sichtbar */}
          <path 
            d="M34.61812,46.06391c-.10035-.04669-.2647-.12802-.48969-.24062-.2666-.13161-.61792-.30632-1.03831-.5087-.13372.02789-.26998.04183-.40666.04162-.9217-.05577-1.75974-.55349-2.24985-1.33597-6.30635-3.08557-17.60695-8.58198-18.25952-8.89865-.34963-.12041-.68108-.28878-.98465-.50004-.30357-.02345-.59848-.11133-.8653-.25794-.72502-.41026-1.26626-1.08183-1.51258-1.87762-.44025-1.06831-.20745-2.29612.59362-3.12888-.00866-1.10401.18696-2.71525,1.35498-3.11515,1.44857-.48103,9.12025-3.28817,9.5736-3.45083.45673-.19942.95043-.29956,1.44857-.29428.51673.00106,1.02838.09908,1.50899.289,1.41899.5537,20.29474,7.48832,22.13941,8.16496l.08471.01901c.59595.15929,1.14647.45567,1.60764.8653,1.06092.97811,1.59307,2.4028,1.43294,3.83679-.14027,2.94192-.45694,3.51146-.82368,3.90946-.02387.02239-.04542.04732-.06401.07436-.08133.14365-.18168.32026-4.47879,3.04755-1.96424,1.24598-3.92848,2.46787-4.15346,2.61152-.83952.65911-1.86368,1.04021-2.92988,1.09028-.51567.00317-1.02522-.11365-1.48807-.34118v.00002ZM37.04421,44.05266c-.19617,1.10346-1.4958.95633-1.4958.95633-.41831-.01574-1.11435-.52508-1.492-.70544h-.01953c-.04903-.02611-.10941-.05381-.18116-.0849l-.04085.02292.46661-.10718c.20248.10124.30565.27294.39234.3106l.13078.01635c.32544.16043.5464.44825.90931.44665.82586-.04843,1.61704-.34796,2.26773-.85874l.04564-.03268c2.84228-1.76492,7.46263-4.68393,7.91642-5.10343.04285-.06557.09207-.12695.14688-.18295.15027-.16322.43107-.46534.56001-3.17051.13213-1.10367-.26645-2.20455-1.07418-2.96803-.19849-.17298-.41791-.32066-.65308-.43924v.01136l-.55024-.20088c-.80155-.29395-19.62693-7.20794-21.03432-7.75798-.35852-.14269-.74056-.21703-1.12639-.21882-.35055-.00598-.69831.06258-1.02037.20088l-.04743.02112c-.31189.11419-7.66172,2.79824-9.0723,3.26518-.17398.07035-.31548.20308-.39679.37227.01953-.02292-.22677-.15637-.20405-.17929.18494-.18275.054-.29922.31387-.28926.54406.05201.04667-.34992.51081-.06154l23.12682,10.17715c1.1232.40316,2.28748,1.52662,2.13622,2.71021M37.21586,42.09096l-1.59914,2.26644c.19245.0169.3811-.06126.50532-.20935.11429-.16605.18464-.35829.20428-.55898v-.03296c.00866-.08471.03105-.25435.05704-.47236s.06401-.50004.09866-.78565c.09866-.80467.19731-1.73059.20597-1.80664.12739-.89614-.41237-1.75277-1.27555-2.02487l-23.32864-10.28644c.01196.02745,0,0,0,0l.77222.44961c1.00789.59426,1.67017,1.63532,1.78087,2.80017v.05197c.08049.62426-.11534,1.25252-.53658,1.72024-.30463.2252-.62362.42969-.95529.61264,1.98325.96564,5.3874,2.60455,8.89696,4.28486,2.46956,1.19063,4.96848,2.38654,7.17503,3.44048l.47067.22499c-.13013-1.13042.48863-2.21436,1.52821-2.67722v-.01394l1.6163-.65425c.19372-.05133.39335-.0769.59362-.07605.35195-.00697.69841.08852.99691.27526h0l.01563.01035c1.1281.67327,1.86643,1.84509,1.98663,3.15318v.05028c.10035.70179-.11872,1.4116-.597,1.93487-.24273.18189-.49708.34794-.76136.49666.17766.05831.36315.08978.55011.09337v-.00021l1.59913-2.26645ZM33.09012,39.24865l-.07605.03105c.77488.46814,1.35371,1.20182,1.62856,2.06458.23259.66714.27991,1.38477.13668,2.07663.16436-.0938.32322-.19647.47595-.30801l.01035-.00866c.08133-.06929.29428-.25266.29935-1.25464-.09739-1.04761-.69672-1.98304-1.60764-2.50927h0c-.15865-.09992-.34392-.14936-.5313-.14196-.11302.0038-.2252.02091-.3342.05049,0,0-.00169-.00021-.0017-.00021ZM13.21261,32.76928c.10668-.06338.20999-.13203.3097-.20597.07436-.06929.25266-.23196.25604-1.05564-.07056-.74995-.44596-1.43822-1.03831-1.90361l-.36336-.24062c-.13415-.08028-.28857-.11999-.44469-.11429-.06654-.00021-.13309.00444-.199.01394.62869.42863,1.09641,1.05458,1.329,1.77897.11365.33505.17766.68489.19034,1.03831.01099.23048-.00232.46159-.03972.68932,0,0,0-.00042,0-.00041ZM25.71103,38.74671c-.27864-.08661-2.30351-1.04866-2.98016-1.37062-.44004-.07816-.75798-.46497-.74932-.91198.01732-.2233.07098-.53299.08661-.63165,0-.3604.28857-.65446.64897-.66101.04774,0,.09528.00486.14196.01394h.06063l.10035.045.04162.01204h0l2.9246,1.31189c.41511.10204.69566.48926.66291.91557-.03105.31836-.07605.62827-.08661.70094-.01986.33526-.29407.59912-.62996.60566-.075.0019-.14978-.00803-.22182-.02936,0,0,.00021-.00042.00022-.00041ZM9.83128,32.16171c-.31498-.66967-.03063-1.468.63693-1.78763.72629-.15253,1.43906.31202,1.59222,1.03831.3154.66925.03169,1.46737-.63503,1.78763-.1014.03443-.20787.05197-.31498.05197-.61559-.05662-1.12852-.49539-1.27956-1.09493,0,0,.00042.00465.00042.00465Z" 
            fill={color}
          />
        </g>
      </svg>
    </div>
  );
};

export default AnimatedFreakSpinner;
